import { CollectionConfig } from "@rebasepro/types";

/**
 * Sender rules — the operator's own verdicts on who is a machine.
 *
 * Ingest recognises most robots by their headers or their address (see
 * backend/src/services/email-kind.ts). The ones it cannot — the tax office
 * writes from `avisocitas@sat.gob.mx` with a person's manners — are one "Es
 * automático" click in Correo, and that click writes a row here so the next
 * message from the same sender is filed right without anyone touching it.
 *
 * A `pattern` is either a whole address or a bare domain. A domain covers its
 * subdomains: silencing `docusign.com` silences `mail.docusign.com`, because
 * the person who silenced a company did not mean "except the hosts it happens
 * to send from".
 *
 * `kind` runs both ways since 0053. Gmail's tabs are read on every poll and
 * a sender Gmail files under Updates would be flipped back five minutes after
 * an operator said otherwise — so "Es una persona" writes a `person` rule,
 * and a `person` rule outranks the tab, the headers and the address.
 *
 * Admin-only and FORCE RLS in the database (drizzle/0051), like every other
 * table Correo owns: the poller reads it through `rebase.dataAsAdmin`, the
 * panel writes it through the data API, and there is no third access model.
 */
const emailSenderRulesCollection: CollectionConfig = {
    name: "Remitentes silenciados",
    singularName: "Remitente automático",
    slug: "email-sender-rules",
    table: "email_sender_rules",
    securityRules: [
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        pattern: {
            name: "Remitente",
            type: "string",
            description: "Una dirección completa (avisocitas@sat.gob.mx) o un dominio (sat.gob.mx). En minúsculas; un dominio cubre sus subdominios.",
            // Unique, as drizzle/0051 created it (email_sender_rules_pattern_key)
            // and as 0055's seed relies on with ON CONFLICT (pattern). Left out,
            // a database built from this config had no such constraint and
            // could not take that migration.
            validation: { required: true, unique: true }
        },
        kind: {
            name: "Se trata como",
            type: "string",
            enum: [
                { id: "automated", label: "Como una máquina", color: "gray" },
                // Outranks everything — Gmail's tab, the headers, the address.
                // Written by "Es una persona", so the click survives the next
                // poll. See drizzle/0053.
                { id: "person", label: "Persona", color: "green" }
            ],
            defaultValue: "automated"
        },
        note: {
            name: "Motivo",
            type: "string",
            description: "Lo que se estaba mirando al escribir la regla. Una regla sin motivo es una regla que nadie se atreve a borrar."
        },
        created_at: {
            name: "Creada",
            type: "date",
            autoValue: "on_create"
        }
    },
    admin: {
        group: "Correo",
        icon: "BellOff",
        sort: ["created_at", "desc"],
        // Written from Correo, one click at a time. A grid of them is only
        // useful for undoing one, and the thread it was written from has the
        // "Es una persona" button for that.
        hideFromNavigation: true
    }
};

export default emailSenderRulesCollection;
